import pako from 'pako';
import { GetBootResponse } from '../contracts';
import { getBootPublicKey } from './boot-public-key';

type JwtHeader = {
  alg?: string;
};

type JwtPayload = {
  d?: string;
} & Record<string, unknown>;

function base64UrlToBase64(value: string): string {
  return value
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(value.length / 4) * 4, '=');
}

function base64ToUint8Array(base64: string): Uint8Array {
  const binary =
    typeof atob !== 'undefined' ? atob(base64) : Buffer.from(base64, 'base64').toString('binary');

  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes;
}

function base64UrlToUint8Array(value: string): Uint8Array {
  return base64ToUint8Array(base64UrlToBase64(value));
}

function base64UrlToString(value: string): string {
  return new TextDecoder().decode(base64UrlToUint8Array(value));
}

function cleanPublicKey(publicKey: string): string {
  return publicKey
    .replace(/-----BEGIN PUBLIC KEY-----/, '')
    .replace(/-----END PUBLIC KEY-----/, '')
    .replace(/\s+/g, '');
}

async function getPublicKey(): Promise<CryptoKey> {
  const cleaned = cleanPublicKey(getBootPublicKey());
  const spkiBytes = base64ToUint8Array(cleaned);
  const spkiDer = spkiBytes.buffer.slice(
    spkiBytes.byteOffset,
    spkiBytes.byteOffset + spkiBytes.byteLength
  ) as ArrayBuffer;

  return crypto.subtle.importKey('spki', spkiDer, { name: 'ECDSA', namedCurve: 'P-256' }, true, [
    'verify',
  ]);
}

function decodeCompressedPayload(d: string): GetBootResponse {
  const compressed = base64UrlToUint8Array(d);
  const decompressed = pako.inflate(compressed);
  const json = new TextDecoder().decode(decompressed);
  return JSON.parse(json) as GetBootResponse;
}

export async function verifyAndDecodeJWT(jwt: string): Promise<GetBootResponse> {
  const [headerB64, payloadB64, signatureB64] = jwt.split('.');

  if (!headerB64 || !payloadB64 || !signatureB64) {
    throw new Error('Malformed JWT');
  }

  const header = JSON.parse(base64UrlToString(headerB64)) as JwtHeader;

  if (header.alg !== 'ES256') {
    throw new Error(`Unsupported alg: ${header.alg}`);
  }

  const publicKey = await getPublicKey();
  const dataToVerify = new TextEncoder().encode(`${headerB64}.${payloadB64}`);
  const signature = base64UrlToUint8Array(signatureB64);

  const isValid = await crypto.subtle.verify(
    { name: 'ECDSA', hash: 'SHA-256' },
    publicKey,
    signature as BufferSource,
    dataToVerify as BufferSource
  );

  if (!isValid) {
    throw new Error('Invalid signature');
  }

  const payload = JSON.parse(base64UrlToString(payloadB64)) as JwtPayload;

  if (!payload.d) {
    throw new Error('Payload does not contain d');
  }

  return decodeCompressedPayload(payload.d);
}
